import type { TRPCLink } from '@trpc/client';
import type { AppRouter } from '@servwell/api/router';
import { PAGE_SESSION } from './idempotency';

/** The timer writes whose tap moment (`tap()`) goes to the server as how long ago it was. */
const TIMER_TAPS = new Set(['costing.startTimer', 'costing.stopTimer']);

interface TapStamp {
  at: Date | string;
  mono?: number;
  session?: string;
}

/**
 * Build-map 062: a timer tap's AGE, read at the moment the request goes out (the first attempt, a
 * retry, or a replay from the offline queue), never at the tap. By the phone's wall clock, whose
 * offset cancels. While the page that made the tap still lives, also by its monotonic clock, and
 * the larger of the two wins: a phone asleep stops the monotonic clock (WebKit) but not the wall
 * clock, and a wall clock moved back is caught by the monotonic one. What is left (a wall clock
 * moved forward while the tap waited) dates the tap too early, never later than it arrived (the
 * server refuses an age over 4 hours). After
 * a reload a negative age (the clock moved back) goes to the server to be refused in words. The
 * tap's own stamps never leave the phone. After the write guard, which keys on the stored input.
 */
export function tapAgeLink(
  clock: { wall: () => number; mono: () => number; session: string } = {
    wall: Date.now,
    mono: () => performance.now(),
    session: PAGE_SESSION,
  },
): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) => {
      if (op.type !== 'mutation' || !TIMER_TAPS.has(op.path)) return next(op);
      const input = op.input as Partial<TapStamp> | null | undefined;
      const at = input?.at;
      if (!(at instanceof Date) && typeof at !== 'string') return next(op);
      const { at: _at, mono, session, ...rest } = input as TapStamp;
      const wallAgo = Math.round(clock.wall() - new Date(at).getTime());
      const agoMs =
        session === clock.session && typeof mono === 'number'
          ? Math.max(wallAgo, Math.round(clock.mono() - mono), 0)
          : wallAgo;
      return next({ ...op, input: { ...rest, agoMs } });
    };
}
